import React, { useState, ReactElement, useRef } from "react";
import { IChartApi, ISeriesApi } from "lightweight-charts";
import { useTranslation } from 'react-i18next';
import { logger } from '../../utils/logger';

interface PointToolProps {
  onPointsChange: (points: { time: number; price: number }[]) => void;
  onSelectModeChange?: (selecting: boolean) => void;
  width?: number;
  height?: number;
  children: ReactElement<{ disableInteraction?: boolean }>;
  chartApi?: IChartApi | null;
  ohlcData?: any[];
  areaSeries?: any; // 传入主图的areaSeries
  hideControls?: boolean; // 新增：隐藏内部控制按钮
  externalSelectMode?: boolean; // 新增：外部控制的选择模式
}

// 暴露给外部的方法接口
export interface PointToolRef {
  clearPoints: () => void;
  setPoint: (time: number, price: number) => void;  // AI Agent 用来设置点
}

const PointTool = React.forwardRef<PointToolRef, PointToolProps>(({
  onPointsChange,
  onSelectModeChange,
  width = 600,
  height = 400,
  children,
  chartApi,
  ohlcData = [],
  areaSeries,
  hideControls = false,
  externalSelectMode = false
}, ref) => {
  const { t } = useTranslation();
  const [selectMode, setSelectMode] = useState(false);
  const [points, setPoints] = useState<{ x: number; y: number }[]>([]);
  const [chartPoints, setChartPoints] = useState<{ time: number; price: number }[]>([]);
  const pointSeriesRef = useRef<ISeriesApi<'Line'> | null>(null);

  // 使用外部控制的选择模式或内部状态
  const currentSelectMode = hideControls ? externalSelectMode : selectMode;

  // 当外部状态变化时，同步内部状态
  React.useEffect(() => {
    if (hideControls && externalSelectMode !== selectMode) {
      setSelectMode(externalSelectMode);
    }
  }, [externalSelectMode, hideControls, selectMode]);

  // 像素x -> K线time
  const getBarTimeFromMouseX = (x: number) => {
    if (!chartApi) return null;
    const t = chartApi.timeScale().coordinateToTime(x);
    logger.log("time", t)
    if (typeof t === 'number') return t;
    if (typeof t === 'string') return Number(t);
    return null;
  };

  // 像素y -> 价格
  const getPriceFromMouseY = (y: number) => {
    if (!areaSeries) return null;
    const price = areaSeries.coordinateToPrice(y);
    logger.log("price", price)
    return price ?? null;
  };

  // K线time -> 最近K线索引
  const getNearestBarIndex = (time: number | null) => {
    if (!ohlcData.length || time == null) return 0;
    let nearest = 0;
    let minDiff = Infinity;
    for (let i = 0; i < ohlcData.length; i++) {
      const diff = Math.abs(ohlcData[i].time - time);
      if (diff < minDiff) {
        minDiff = diff;
        nearest = i;
      }
    }
    return nearest;
  };

  const getRelativePos = (e: React.MouseEvent) => {
    const target = e.currentTarget as HTMLElement;
    const rect = target.getBoundingClientRect();
    return {
      x: e.clientX - rect.left,
      y: e.clientY - rect.top,
    };
  };

  const handleChartClick = (e: React.MouseEvent) => {
    if (!currentSelectMode || !chartApi || !ohlcData.length) return;
    
    const pos = getRelativePos(e);
    const time = getBarTimeFromMouseX(pos.x);
    const idx = getNearestBarIndex(time);
    if (time !== null && ohlcData[idx]) {
      const price = ohlcData[idx].close; // 绑定到K线close价
      // 更新 chartPoints 状态
      const newChartPoint = { time, price };
      setChartPoints([newChartPoint]);
      
      // 在 lightweight chart 上创建点标记
      if (chartApi) {
        if (pointSeriesRef.current) {
          chartApi.removeSeries(pointSeriesRef.current);
          pointSeriesRef.current = null;
        }
        const pointSeries = chartApi.addLineSeries({
          color: "rgba(30,144,255,0.7)",
          lineWidth: 1,
          priceLineVisible: false,
          lastValueVisible: false,
        });
        pointSeries.setData([{ time: time as any, value: price }]);
        pointSeries.setMarkers([
          {
            time: time as any,
            position: 'inBar',
            color: "rgba(30, 143, 255, 0.7)",
            shape: 'circle',
            size: 1.5,
          }
        ]);
        pointSeriesRef.current = pointSeries;
      }
      // 保持原有的像素坐标用于 UI 显示
      const newPoints = [pos];
      setPoints(newPoints);
      // 这里传递 time/price，而不是像素坐标
      onPointsChange([newChartPoint]);
    }
  };

  // 清除点标记
  const clearPoints = () => {
    if (pointSeriesRef.current && chartApi) {
      chartApi.removeSeries(pointSeriesRef.current);
      pointSeriesRef.current = null;
    }
    setPoints([]);
    setChartPoints([]);
    // 通知外部组件点已被清除
    onPointsChange([]);
  };

  const handleSelectMode = (mode: boolean) => {
    setSelectMode(mode);
    if (onSelectModeChange) onSelectModeChange(mode);
  };

  const chartWithProps = children;

  // 程序化设置点（AI Agent 用）
  const setPoint = (time: number, price: number) => {
    if (!chartApi) {
      return;
    }
    
    // 更新 chartPoints 状态
    const newChartPoint = { time, price };
    setChartPoints([newChartPoint]);
    
    // 在 lightweight chart 上创建点标记
    if (pointSeriesRef.current) {
      chartApi.removeSeries(pointSeriesRef.current);
      pointSeriesRef.current = null;
    }
    
    const pointSeries = chartApi.addLineSeries({
      color: "rgba(30,144,255,0.7)",
      lineWidth: 1,
      priceLineVisible: false,
      lastValueVisible: false,
    });
    
    pointSeries.setData([{ time: time as any, value: price }]);
    
    pointSeries.setMarkers([
      {
        time: time as any,
        position: 'inBar',
        color: "rgba(30, 143, 255, 0.7)",
        shape: 'circle',
        size: 1.5,
      }
    ]);
    pointSeriesRef.current = pointSeries;
    
    // 通知外部组件
    onPointsChange([newChartPoint]);
  };

  React.useImperativeHandle(ref, () => ({
    clearPoints: clearPoints,
    setPoint: setPoint,
  }), [chartApi, onPointsChange]);  // 添加依赖数组确保函数使用最新的 chartApi

  return (
    <div style={{ position: "relative", width, height }}>
      {/* 工具按钮 */}
      {!hideControls && (
        <div style={{ marginBottom: 8 }}>
          <button
            style={{
              background: currentSelectMode ? "#d0f0d0" : "#f0f0f0",
              border: "1px solid #888",
              borderRadius: 6,
              padding: "6px 16px",
              cursor: "pointer",
              marginRight: 8,
            }}
            onClick={() => handleSelectMode(!currentSelectMode)}
          >
            {currentSelectMode ? t('tools.exitPointTool') : t('tools.pointTool')}
          </button>
          {points.length > 0 && (
            <button
              style={{
                background: "#f0f0f0",
                border: "1px solid #888",
                borderRadius: 6,
                padding: "6px 16px",
                cursor: "pointer",
              }}
              onClick={() => {
                clearPoints();
                onPointsChange([]);
              }}
            >
              移除点
            </button>
          )}
        </div>
      )}
      {/* 图表容器 */}
      <div
        style={{ width, height, position: "relative", border: "none" }}
        onClick={handleChartClick}
      >
        {chartWithProps}
      </div>
    </div>
  );
});

export default PointTool; 
